import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import {
  Mail, Search, CheckCircle2, XCircle, Clock, ExternalLink,
  Loader2, Send, Filter, RefreshCw, MailOpen, AlertTriangle, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import { getEmailHistory, getEmailStats } from '../services/api';
import { useRegion } from '../context/RegionContext';

export default function EmailHistoryPage() {
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const { region } = useRegion();

  const fetchEmails = async (resetPage = false) => {
    try {
      setLoading(true);
      const skip = resetPage ? 0 : page * 20;
      if (resetPage) setPage(0);
      const params = { region, limit: 20, skip };
      if (search.trim()) params.search = search.trim();
      const data = await getEmailHistory(params);
      setEmails(data.emails || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Failed to load email history');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await getEmailStats(region);
      setStats(data);
    } catch (e) {}
  };

  useEffect(() => {
    fetchEmails(true);
    fetchStats();
  }, [region]);

  const handleSearch = () => fetchEmails(true);

  const getStatusBadge = (status) => {
    if (status === 'sent') return (
      <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1 text-xs">
        <CheckCircle2 className="h-3 w-3" /> Sent
      </Badge>
    );
    return (
      <Badge className="bg-red-500/10 text-red-600 border-red-500/20 gap-1 text-xs">
        <XCircle className="h-3 w-3" /> Failed
      </Badge>
    );
  };

  const getInterviewStatusBadge = (status) => {
    if (!status) return null;
    const colors = {
      pending: 'bg-muted text-muted-foreground',
      in_progress: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
      completed: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
    };
    return (
      <Badge className={`${colors[status] || colors.pending} text-xs gap-1`}>
        {status === 'completed' ? <CheckCircle2 className="h-3 w-3" /> : 
         status === 'in_progress' ? <Clock className="h-3 w-3" /> : null}
        Interview: {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="email-history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Email History
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track all interview invitation emails sent from the platform
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchEmails(true); fetchStats(); }} data-testid="refresh-emails-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Mail className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Emails</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Send className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.sent}</p>
              <p className="text-xs text-muted-foreground">Sent Successfully</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.failed}</p>
              <p className="text-xs text-muted-foreground">Failed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by email, subject..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-9"
            data-testid="email-search-input"
          />
        </div>
        <Button variant="outline" onClick={handleSearch} data-testid="email-search-btn">
          <Filter className="h-4 w-4 mr-1" /> Search
        </Button>
      </div>

      {/* Email List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MailOpen className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No emails sent yet</p>
              <p className="text-xs mt-1">Interview invitation emails will appear here once sent.</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[600px]">
              <div className="divide-y">
                {emails.map((email) => (
                  <div key={email.id} className="p-4 hover:bg-muted/30 transition-colors" data-testid={`email-row-${email.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getStatusBadge(email.status)}
                          {email.interview_status && getInterviewStatusBadge(email.interview_status)}
                        </div>
                        <p className="text-sm font-medium truncate">{email.subject}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {email.to_email}
                          </span>
                          {email.cc_email && (
                            <span className="flex items-center gap-1">CC: {email.cc_email}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          {email.job_title && (
                            <span className="font-medium text-foreground/70">{email.job_title}</span>
                          )}
                          {email.candidate_name && (
                            <span>{email.candidate_name}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-muted-foreground">
                          {email.sent_at ? new Date(email.sent_at).toLocaleString() : 'N/A'}
                        </p>
                        {email.interview_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs gap-1 mt-1"
                            onClick={() => window.open(email.interview_url, '_blank')}
                          >
                            <Link2 className="h-3 w-3" /> View Link
                          </Button>
                        )}
                      </div>
                    </div>
                    {email.error && (
                      <div className="mt-2 p-2 bg-red-500/5 border border-red-500/10 rounded text-xs text-red-600">
                        {email.error}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 20 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {page * 20 + 1}-{Math.min((page + 1) * 20, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => { setPage(p => p - 1); fetchEmails(); }}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * 20 >= total} onClick={() => { setPage(p => p + 1); fetchEmails(); }}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
